// Public marketing landing page — Commissions by Staffology, part of Staffology OS.
// Sections: Header → Hero → Suite → Why → How → Contact → Footer.
import React from 'react';
import { Icon } from './ui.jsx';
import { Logo } from './Logo.jsx';

// Sister apps in the Staffology OS suite.
const CRM_URL = 'https://crm.staffologyos.com';
const SUITE_URL = 'https://staffologyos.com';

// ----------------------------------------------------------------------------
// Shared
// ----------------------------------------------------------------------------
function Wordmark({ size = 28 }) {
  return (
    <div className="inline-flex items-center gap-2 sm:gap-2.5 min-w-0">
      <Logo size={size}/>
      <div className="text-[12.5px] sm:text-[15px] font-semibold text-neutral-900 tracking-[-0.01em] leading-tight truncate">
        Commissions <span className="text-neutral-400 font-normal">by</span> <span className="text-primary-900">Staffology</span>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Top-level
// ----------------------------------------------------------------------------
export function LandingPage({ onSignIn, isSignedIn }) {
  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Header  onSignIn={onSignIn} isSignedIn={isSignedIn}/>
      <Hero    onSignIn={onSignIn} isSignedIn={isSignedIn}/>
      <SuiteApps/>
      <WhyKPIs/>
      <HowItWorks/>
      <Contact onSignIn={onSignIn} isSignedIn={isSignedIn}/>
      <Footer/>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Header — wordmark + 3 nav links + a single Sign in CTA
// ----------------------------------------------------------------------------
function Header({ onSignIn, isSignedIn }) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 transition-all ${scrolled
      ? 'bg-white/85 backdrop-blur-lg border-b border-neutral-200 shadow-sm'
      : 'bg-white border-b border-transparent'}`}>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-[60px] sm:h-[68px] flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Wordmark/>
          <a href={SUITE_URL} className="hidden lg:inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-600 hover:border-neutral-400 hover:text-neutral-900 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-700"/> Part of Staffology OS
          </a>
        </div>
        <nav className="hidden md:flex items-center gap-7 text-[13.5px] font-medium text-neutral-600">
          <a href="#suite"   className="hover:text-neutral-900">The suite</a>
          <a href="#why"     className="hover:text-neutral-900">Why us</a>
          <a href="#how"     className="hover:text-neutral-900">How it works</a>
          <a href="#contact" className="hover:text-neutral-900">Contact</a>
        </nav>
        <button onClick={onSignIn}
          className="group inline-flex items-center gap-1.5 h-9 px-4 sm:px-5 rounded-md bg-neutral-900 hover:bg-black text-white text-[13px] sm:text-[13.5px] font-semibold shadow-sm whitespace-nowrap">
          {isSignedIn ? 'Open dashboard' : 'Sign in'}
          <Icon name="arrow" size={14} className="transition-transform group-hover:translate-x-0.5"/>
        </button>
      </div>
    </header>
  );
}

// ----------------------------------------------------------------------------
// Hero — headline + sub + CTAs + product mock
// ----------------------------------------------------------------------------
function Hero({ onSignIn, isSignedIn }) {
  return (
    <section className="relative overflow-hidden pt-10 sm:pt-16 md:pt-24 pb-14 sm:pb-20 md:pb-28">
      {/* faint grid background */}
      <div className="absolute inset-0 -z-20 opacity-[0.06]" style={{
        backgroundImage: 'linear-gradient(#1A1A1A 1px, transparent 1px), linear-gradient(90deg, #1A1A1A 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }}/>
      {/* soft spotlight */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-gradient-to-b from-primary-100 via-primary-100/30 to-transparent rounded-full blur-3xl opacity-70"/>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.05fr_1fr] gap-8 sm:gap-12 items-center">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-700 mb-4">Staffology OS · the sales &amp; marketing suite for staffing agencies</div>
          <h1 className="text-[34px] sm:text-[42px] md:text-[56px] lg:text-[64px] font-bold tracking-[-0.03em] leading-[1.02]">
            Commission, <span className="text-primary-700">done right.</span>
          </h1>
          <p className="mt-5 sm:mt-6 text-[15.5px] sm:text-[17px] md:text-[18px] text-neutral-600 leading-relaxed max-w-[520px]">
            Upload two files. Get a payroll-ready commission run for every sales rep. Built for staffing agencies —
            and part of Staffology OS, the same workspace as your CRM.
          </p>
          <div className="mt-7 sm:mt-9 flex flex-wrap items-center gap-3">
            <button onClick={onSignIn}
              className="group inline-flex items-center gap-2 h-12 px-6 bg-neutral-900 hover:bg-black text-white text-[14.5px] sm:text-[15px] font-semibold rounded-lg shadow-[0_4px_14px_rgba(0,0,0,0.15)] transition">
              {isSignedIn ? 'Open your dashboard' : 'Sign in'}
              <Icon name="arrow" size={15} className="transition-transform group-hover:translate-x-0.5"/>
            </button>
            <a href="#how"
              className="inline-flex items-center h-12 px-5 rounded-lg border border-neutral-200 bg-white hover:border-neutral-400 text-[14px] font-medium text-neutral-800 transition">
              How it works
            </a>
          </div>
        </div>

        <div className="relative lg:pl-8">
          <ProductMock/>
        </div>
      </div>
    </section>
  );
}

function ProductMock() {
  return (
    <div className="relative bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-neutral-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500">March 2026 · Run detail</div>
            <div className="text-[20px] font-bold text-neutral-900 mt-0.5 tracking-[-0.01em]">Your commission</div>
          </div>
          <div className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-semibold uppercase tracking-wider">Approved</div>
        </div>
        <div className="text-[36px] sm:text-[42px] md:text-[46px] font-bold font-mono tabular-nums text-neutral-900 tracking-[-0.02em] leading-none">
          $12,847<span className="text-neutral-400 text-[20px]">.33</span>
        </div>
        <div className="text-[12px] text-neutral-500 mt-2">From 137 invoices across 18 clients</div>
        <div className="mt-5 pt-5 border-t border-neutral-100 space-y-1.5">
          {[
            ['Gross Profit',         '$186,420.00', 'text-neutral-900'],
            ['minus ACA (1.80%)',    '−$3,355.56',  'text-red-700'],
            ['minus SICK (1.20%)',   '−$2,237.04',  'text-red-700'],
            ['= NET',                '$180,827.40', 'text-neutral-900 font-semibold'],
            ['× Your rate (Y2+ 7%)', '$12,657.92',  'text-primary-700 font-semibold'],
          ].map(([l, v, cls]) => (
            <div key={l} className="flex items-center justify-between text-[12.5px] py-1.5">
              <span className="text-neutral-700">{l}</span>
              <span className={`font-mono tabular-nums ${cls}`}>{v}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center gap-2">
          <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-neutral-200 text-[11.5px] font-semibold text-neutral-700"><Icon name="download" size={12}/> Excel</button>
          <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-neutral-200 text-[11.5px] font-semibold text-neutral-700"><Icon name="download" size={12}/> PDF</button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Suite — Commissions is one app of Staffology OS
// ----------------------------------------------------------------------------
function SuiteApps() {
  const apps = [
    { name: 'CRM by Staffology', status: 'Available now', href: CRM_URL,
      body: 'Sell. Leads, pipeline, cadences, quotes with e-signature and playbooks — gamified so reps log the work.' },
    { name: 'Commissions by Staffology', status: "You're here", here: true,
      body: 'Pay reps right. Two files in, a payroll-ready commission run out — per-rep rates, approvals, audit-ready statements.' },
    { name: 'Marketing by Staffology', status: 'Coming soon', soon: true,
      body: 'Fill the top of the funnel. Web-to-lead forms, lists and campaigns that hand warm leads straight to the CRM.' },
  ];
  return (
    <section id="suite" className="py-14 sm:py-20 md:py-24 border-y border-neutral-200 bg-canvas">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
        <div className="max-w-[620px] mb-10 sm:mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700 mb-3">The suite</div>
          <h2 className="text-[24px] sm:text-[30px] md:text-[38px] lg:text-[44px] font-bold text-neutral-900 tracking-[-0.02em] leading-tight">
            One workspace. Apps you switch on.
          </h2>
          <p className="mt-4 text-[15px] text-neutral-600 leading-relaxed">
            Staffology OS is one login, one team, one audit trail. Each app is switched on per workspace, so your agency only sees what it uses.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          {apps.map(a => {
            const inner = (
              <>
                <div className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${a.soon ? 'text-neutral-500' : 'text-primary-700'}`}>{a.status}</div>
                <div className="text-[17px] font-semibold text-neutral-900 mt-3">{a.name}</div>
                <div className="text-[13.5px] text-neutral-600 mt-2 leading-relaxed">{a.body}</div>
                {a.href && <div className="mt-4 text-[13px] font-medium text-neutral-800 inline-flex items-center gap-1.5">Open the CRM <Icon name="arrow" size={13}/></div>}
              </>
            );
            const cls = `block rounded-2xl border p-6 transition ${a.here ? 'bg-white border-primary-300 shadow-sm' : a.soon ? 'bg-white/60 border-dashed border-neutral-300' : 'bg-white border-neutral-200 hover:border-primary-300 hover:shadow-sm'}`;
            return a.href
              ? <a key={a.name} href={a.href} className={cls}>{inner}</a>
              : <div key={a.name} className={cls}>{inner}</div>;
          })}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Why — three KPIs
// ----------------------------------------------------------------------------
function WhyKPIs() {
  const items = [
    { value: '~16 hrs', label: 'saved per run',  body: 'The manual VLOOKUP + pivot-table ritual is gone. A month-end run takes under 15 minutes.' },
    { value: 'Zero',    label: 'math errors',    body: 'Every invoice traces back to the same formula. Rates are snapshotted per run, never ad-hoc.' },
    { value: '100%',    label: 'audit-ready',    body: 'Every override, approval, and unlock is logged with actor and timestamp — SOX-friendly.' },
  ];
  return (
    <section id="why" className="py-14 sm:py-20 md:py-24 border-y border-neutral-200 bg-white">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
        <div className="max-w-[560px] mb-10 sm:mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700 mb-3">Why teams use it</div>
          <h2 className="text-[24px] sm:text-[30px] md:text-[38px] lg:text-[44px] font-bold text-neutral-900 tracking-[-0.02em] leading-tight">
            Replace two days of spreadsheet work.
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
          {items.map(k => (
            <div key={k.label} className="rounded-2xl border border-neutral-200 p-5 sm:p-6 hover:border-primary-300 transition">
              <div className="text-[28px] sm:text-[32px] md:text-[36px] font-bold font-mono tabular-nums text-primary-700 tracking-[-0.02em] leading-none">
                {k.value}
              </div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mt-2">{k.label}</div>
              <div className="text-[13.5px] text-neutral-600 mt-3 leading-relaxed">{k.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// How it works — three steps
// ----------------------------------------------------------------------------
function HowItWorks() {
  const steps = [
    { n: '01', title: 'Upload two files', body: 'Drop in your Transaction export and AR aging report. Columns map automatically.' },
    { n: '02', title: 'Assign your reps', body: 'Pick the sales rep for each client. Per-rep rate overrides apply automatically.' },
    { n: '03', title: 'Share & finalize', body: 'Each rep sees their own commission. Download Excel or PDF. Finalize for payroll.' },
  ];
  return (
    <section id="how" className="py-14 sm:py-20 md:py-28 bg-canvas border-y border-neutral-200">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
        <div className="max-w-[600px] mb-10 sm:mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700 mb-3">How it works</div>
          <h2 className="text-[24px] sm:text-[30px] md:text-[38px] lg:text-[44px] font-bold text-neutral-900 tracking-[-0.02em] leading-tight">
            From file to finalized in three steps.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          {steps.map(s => (
            <div key={s.n} className="bg-white border border-neutral-200 rounded-2xl p-6 hover:border-primary-300 hover:shadow-sm transition">
              <div className="text-[11px] font-mono font-bold text-primary-700 mb-3">{s.n}</div>
              <div className="text-[16px] font-semibold text-neutral-900 mb-2">{s.title}</div>
              <div className="text-[13.5px] text-neutral-600 leading-relaxed">{s.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Contact — form + sign-in panel
// ----------------------------------------------------------------------------
function Contact({ onSignIn, isSignedIn }) {
  const [sent, setSent] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', email: '', company: '', message: '' });

  const onSubmit = (e) => {
    e.preventDefault();
    // TODO: hook to a real endpoint (Edge Function or Formspree).
    console.log('[contact]', form);
    setSent(true);
  };

  return (
    <section id="contact" className="py-14 sm:py-20 md:py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700 mb-3">Get in touch</div>
          <h2 className="text-[26px] sm:text-[32px] md:text-[40px] lg:text-[48px] font-bold text-neutral-900 tracking-[-0.025em] leading-[1.05]">
            Questions, or a staffing agency we should know?
          </h2>
          <p className="mt-5 text-[15px] text-neutral-600 leading-relaxed max-w-[520px]">
            Running more than one LLC? Want a custom rate structure we haven't built yet? Looking to pilot before committing? Send a note and you'll hear back the same day.
          </p>

          <div className="mt-6 flex items-center gap-4 text-[13px]">
            <a href="mailto:hello@staffology.app" className="font-medium text-neutral-700 hover:text-neutral-900">hello@staffology.app</a>
            <span className="text-neutral-300">·</span>
            <span className="text-neutral-500">Replies in &lt; 1 business day</span>
          </div>

          <form onSubmit={onSubmit} className="mt-8 bg-canvas border border-neutral-200 rounded-2xl p-6">
            {sent ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center mx-auto mb-3">
                  <Icon name="check" size={22}/>
                </div>
                <div className="text-[16px] font-semibold text-neutral-900">Thanks — we'll be in touch</div>
                <div className="text-[13px] text-neutral-500 mt-1">Usually within a business day.</div>
                <button onClick={() => { setSent(false); setForm({ name: '', email: '', company: '', message: '' }); }}
                  className="mt-4 text-[12px] text-primary-700 hover:text-primary-900 font-medium">
                  Send another
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <input required placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="h-11 px-3 text-[14px] rounded-lg border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                  <input required type="email" placeholder="Work email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className="h-11 px-3 text-[14px] rounded-lg border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                </div>
                <input placeholder="Company (optional)" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                  className="w-full h-11 px-3 text-[14px] rounded-lg border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 mb-3"/>
                <textarea required rows={4} placeholder="What are you trying to solve?" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                  className="w-full px-3 py-3 text-[14px] rounded-lg border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 resize-none"/>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="text-[11px] text-neutral-500">We'll never share your info.</div>
                  <button type="submit" className="group inline-flex items-center gap-2 h-11 px-5 bg-neutral-900 hover:bg-black text-white text-[14px] font-semibold rounded-lg transition">
                    Send message
                    <Icon name="arrow" size={14} className="transition-transform group-hover:translate-x-0.5"/>
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        {/* Already-on-the-team panel — only sign-in CTA on this page besides the header */}
        <div className="lg:sticky lg:top-24">
          <div className="bg-gradient-to-br from-primary-900 to-primary-700 text-white rounded-2xl p-7 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[260px] h-[260px] bg-white/10 rounded-full -translate-y-1/3 translate-x-1/3"/>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 text-[10px] font-bold uppercase tracking-wider mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>
                Already on the team?
              </div>
              <div className="text-[22px] md:text-[24px] font-bold tracking-[-0.015em] leading-tight">
                Sign in with your work email.
              </div>
              <p className="mt-3 text-[13px] text-white/80 leading-relaxed">
                If your admin has already added you, you're good to go. Use the password they shared, or reset it via the login screen.
              </p>
              <button onClick={onSignIn}
                className="group mt-6 w-full inline-flex items-center justify-center gap-2 h-12 bg-white text-primary-900 text-[15px] font-semibold rounded-lg hover:bg-neutral-100 transition shadow-lg">
                {isSignedIn ? 'Open your dashboard' : 'Sign in'}
                <Icon name="arrow" size={16} className="transition-transform group-hover:translate-x-0.5"/>
              </button>
              <div className="mt-5 pt-5 border-t border-white/10 text-[11.5px] text-white/70 flex items-center gap-1.5">
                <Icon name="lock" size={12}/>
                Admin-invited access only · no public signup
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Footer
// ----------------------------------------------------------------------------
function Footer() {
  return (
    <footer className="bg-white border-t border-neutral-200">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <Wordmark size={24}/>
        <div className="flex items-center gap-5 text-[12px] text-neutral-500 flex-wrap justify-center">
          <a href={SUITE_URL} className="hover:text-neutral-800 font-medium">Staffology OS</a>
          <a href={CRM_URL}   className="hover:text-neutral-800">CRM</a>
          <a href="#why"     className="hover:text-neutral-800">Why us</a>
          <a href="#how"     className="hover:text-neutral-800">How it works</a>
          <a href="#contact" className="hover:text-neutral-800">Contact</a>
        </div>
        <div className="text-[11.5px] text-neutral-500">
          © {new Date().getFullYear()} Staffology
        </div>
      </div>
    </footer>
  );
}
